/**
 * Sorting helpers shared by the admin overview list dialogs (users, subscribers, trips).
 * Each dialog supplies accessors so the comparison logic stays in one place.
 */

const DEFAULT_DIRECTIONS = { date: 'desc', number: 'desc' };

export const sortDirectionFor = (field, kinds = {}) => (
  DEFAULT_DIRECTIONS[kinds[field]] || 'asc'
);

export const initialSortState = (field, kinds = {}) => ({
  field,
  direction: sortDirectionFor(field, kinds),
});

// Clicking the active field flips direction; a new field starts in its natural order.
export const nextSortState = (previous, field, kinds = {}) => (
  previous?.field === field
    ? { field, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
    : initialSortState(field, kinds)
);

export const toTimestamp = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const compareValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a ?? '').toLowerCase().localeCompare(String(b ?? '').toLowerCase());
};

/**
 * @param {Array} items rows to sort
 * @param {Object} accessors map of field -> (item) => string | number
 * @param {string} field active sort field
 * @param {string} direction 'asc' | 'desc'
 * @param {Function} [tieBreaker] accessor used when the active field compares equal
 */
export const sortRecords = (items, accessors, field, direction = 'asc', tieBreaker) => {
  const accessor = accessors?.[field];
  if (!accessor) return [...(items || [])];
  const factor = direction === 'asc' ? 1 : -1;
  return [...(items || [])].sort((a, b) => {
    const compared = compareValues(accessor(a), accessor(b));
    if (compared !== 0) return compared * factor;
    if (!tieBreaker) return 0;
    return compareValues(tieBreaker(a), tieBreaker(b));
  });
};

export const formatListDate = (value, { withTime = false } = {}) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
};
